/*
 * Тексты панели плана изменения и окна публикации по плану (005, US5, T122; spec.md FR-037, FR-038, FR-060). Язык —
 * ASD-STE100, как у страницы «Правила расчёта» (`texts.ts`): короткие предложения, нейтральный род, одно слово — одно
 * значение («флажок», «ведущий черновика», «проверочный пример»). Тексты состояний плана «устарел…» и «опубликован…»
 * приходят с сервера (`PlanResponse.stateText`): здесь — только запасные слова, если поля нет. Проверка —
 * `RulesPlan.test.tsx`, «язык текстов плана» (`@tabel/shared/plain-language`).
 */

/** Предложения панели и окна. */
export const PLAN_T = {
  sameData: 'Данные не менялись после подготовки. Числа те же.',
  freshData: 'Данные изменились после подготовки. Публикация пойдёт по новым числам.',
  recompute: 'Данные изменились после подготовки. Новые числа ещё не посчитаны.',
  recomputeError: 'Влияние не посчитано. Нажмите «Посчитать влияние заново» ещё раз.',
  basisEditable: 'Основание можно изменить в окне публикации.',
  goldensHint: 'Флажок «Так и должно быть» ставится в окне публикации.',
  risksHint: 'Флажок «Понимаю риск» ставится в окне публикации.',
  goldenConfirmed: 'подтверждено: «Так и должно быть»',
  goldenToConfirm: 'нужен флажок «Так и должно быть»',
  riskAcked: 'риск принят: «Понимаю риск»',
  riskToAck: 'нужен флажок «Понимаю риск»',
  approverOnly: 'Вы можете только смотреть проект. Опубликовать его может ведущий черновика.',
  leaderOnlyNoName: 'Опубликовать проект может только ведущий черновика.',
  notFound: 'Такого проекта нет. Проверьте ссылку или попросите агента подготовить проект заново.',
  forbidden: 'Проект может открыть администратор правил или утверждающий.',
  staleFallback: 'Проект устарел. Попросите агента подготовить проект заново.',
  publishedFallback: 'Проект опубликован.',
  dateFixed: 'Эта дата — из проекта. Для другой даты попросите агента подготовить проект заново.',
  dialogBasis: 'Основание предложил ИИ-агент. Его можно изменить.',
  listIntro:
    'Здесь — проекты новой версии правил, которые подготовили ИИ-агенты. Агент даёт ссылку на проект, но проект можно открыть и отсюда. Публикует человек.',
  listEmpty: 'Проектов нет. Проект готовит ИИ-агент, когда доводит изменение правил до конца.',
} as const;

/** Подписи панели, разделов и кнопок — до 6 слов. */
export const PLAN_LABEL = {
  panel: 'Проект новой версии правил',
  prepared: 'Подготовлено ИИ-агентом',
  facts: 'Сведения о проекте',
  client: 'Клиент агента',
  frozenAt: 'Когда подготовлен',
  expiresAt: 'Опубликовать можно до',
  number: 'Номер проекта',
  validFrom: 'Новые правила действуют с',
  state: 'Состояние проекта',
  changes: 'Что изменится',
  basis: 'Основание, которое предложил агент',
  impact: 'Влияние на табели',
  frozen: 'При подготовке',
  now: 'Сейчас',
  goldens: 'Проверочные примеры, которые нужно подтвердить',
  risks: 'Риски, которые нужно принять',
  warnings: 'Предупреждения проверки правил',
  publish: 'Опубликовать проект',
  recompute: 'Посчитать влияние заново',
  openVersion: 'Открыть в истории изменений',
  close: 'Закрыть',
  loadError: 'Проект не загрузился',
  tab: 'Проекты от ИИ-агентов',
  open: 'Открыть проект',
  listError: 'Проекты не загрузились',
  stateActive: 'Готов',
  stateStale: 'Устарел',
  stateExpired: 'Срок прошёл',
  statePublished: 'Опубликован',
} as const;

/** Тексты с подстановкой. Время — уже по местному времени объекта: «24.10.2026 10:00». */
export const sayPlan = {
  /** Состояние действующего проекта (план изменения): до какого времени его можно опубликовать. */
  active: (until: string) => `Проект готов. Опубликовать можно до ${until}.`,
  /** Строка списка и полосы черновика: номер плана. */
  title: (n: number) => `Проект №${n}`,
  /** Дата начала новых правил по плану. */
  validFrom: (date: string) => `Новые правила действуют с ${date}.`,
  /** Кто и когда заморозил план. */
  frozen: (client: string, at: string) => `Подготовил ИИ-агент: ${client}, ${at}.`,
  /** Полоса черновика: у черновика есть действующий план агента. */
  draftHas: (n: number, from: string, until: string) =>
    `Проект новой версии правил №${n} от ИИ-агента готов: новые правила с ${from}. Опубликовать можно до ${until}.`,
  /** Метка в окне публикации: клиент агента и время заморозки. */
  prepared: (client: string, at: string) => `Подготовлено ИИ-агентом: ${client}, ${at}.`,
  /** Только чтение у администратора правил, который не ведёт черновик. */
  leaderOnly: (name: string | null) =>
    name ? `Опубликовать проект может только ведущий черновика: ${name}.` : PLAN_T.leaderOnlyNoName,
};

/** Текст сервера как предложение: точка в конце, если её нет. */
export function asSentence(s: string): string {
  const t = s.trim();
  return /[.!?…]$/u.test(t) ? t : `${t}.`;
}
